import React from 'react';

import { Alert, Box, Checkbox, Link } from '@amzn/awsui-components-react-v3';

import { isObject } from 'lodash';
import { RECEIVE_ERROR_MESSAGES_MAP } from 'src/constants';
import { AsyncState } from 'src/store/types';

interface MPNReceiveErrorsProps {
  genericErrors: any; // errors mapped from src/constants{RECEIVE_ERROR_MESSAGES_MAP}
  invalidContainers: string[];
  containersNotInUserCustody: string[];
  totalErrors: number;
}

interface MPNOverageWarningProps {
  isBlockingOverage: boolean;
  overageSerials: number;
}

interface MPNPOReceiveWarningProps {
  status: AsyncState | undefined;
  setIsEmptyPOLinesWarningAcknowledged: any;
  isEmptyPOLinesWarningAcknowledged: any;
}

interface MPNsToReceiveStatusProps
  extends MPNReceiveErrorsProps,
    Omit<MPNOverageWarningProps, 'overageSerials'>,
    Omit<MPNPOReceiveWarningProps, 'status'> {
  status?: AsyncState;
  error: any;
  isAdjustment: boolean;
  isRMA: boolean;
  overrage: boolean;
  isRequireManualPOReceiveWarning: boolean;
  currentReceivedQty: number;
  receivingQty: number;
  expectedQty: number;
}

const MPNReceiveSucess = () => (
  <Alert statusIconAriaLabel="Success" type="success">
    Model has been received successfully.
  </Alert>
);

const MPNReceiveGenericError = () => (
  <Alert statusIconAriaLabel="Error" type="error" header="Error procesing the request">
    Something went wrong while processing the request, please resubmit
  </Alert>
);

const MPNOverageWarning = ({ isBlockingOverage, overageSerials }: MPNOverageWarningProps) => (
  <Alert type={isBlockingOverage ? 'error' : 'warning'} statusIconAriaLabel="warning" header="Overage">
    There is an overage of ({overageSerials}) serials for this model.
  </Alert>
);

export const UnExpectedTagsWarning = ({ unExpectedTags }: { unExpectedTags: [] }) => {
  return (
    <>
      {unExpectedTags.length > 0 && (
        <Alert statusIconAriaLabel="Error" type="error">
          <span>
            {' '}
            {'Unexpected tags '} {<b>({unExpectedTags.join(', ')})</b>}
            {' found. Please cut a ticket '}{' '}
          </span>
          <Link external href="https://t.corp.amazon.com/create/templates/815ef828-49c6-476b-9342-b37f29b9732b">
            here.
          </Link>
        </Alert>
      )}
    </>
  );
};

const ContainersNotinUserCustodyError = ({ containersNotInUserCustody }: { containersNotInUserCustody: string[] }) => {
  return (
    <p>
      Container(s) are not in user custody: {containersNotInUserCustody.join(',')}. Try re-receiving in a container that
      is in your custody.
    </p>
  );
};

const InvalidContainersError = ({ invalidContainers }: { invalidContainers: string[] }) => (
  <p>
    Container(s) are not valid: {invalidContainers.join(',')}. Try re-receiving the respective parts with a valid
    container.
  </p>
);

const CommonErrorCreateCTI = () => (
  <p>
    Cut a ticket
    <Box variant="span" margin={{ left: 'xxs', right: 'xxs' }}>
      <a
        href="https://cti.amazon.com/cti/category/AWS/type/SC.os%20Warehouse/item/Unified%20Receive"
        target="_blank"
        rel="noreferrer"
      >
        here
      </a>
    </Box>
    if the issue still exists.
  </p>
);

const MPNReceieveCommonErrors = ({ otherErrors = [] }) => (
  <>
    {otherErrors?.map((err: any, i: number) => {
      return (
        <div key={i}>
          {!isObject(err) && (
            <p>
              {RECEIVE_ERROR_MESSAGES_MAP?.[err]
                ? RECEIVE_ERROR_MESSAGES_MAP?.[err]
                : RECEIVE_ERROR_MESSAGES_MAP.COMMON_ERROR}
            </p>
          )}
        </div>
      );
    })}
  </>
);

const MPNPOReceiveWarning = ({
  status,
  setIsEmptyPOLinesWarningAcknowledged,
  isEmptyPOLinesWarningAcknowledged,
}: MPNPOReceiveWarningProps) => {
  return (
    <Alert statusIconAriaLabel="Warning" type="warning" header={`Shipment is missing PO line item number`}>
      <Box>
        <Box variant="span">After successfully ingesting the items, ensure quantity is financially received in </Box>
        <Link href="https://aws.argo.ocean-wave.aws.a2z.com/ordering/search/" target="_blank">
          SC.os receipt
        </Link>
      </Box>
      <Checkbox
        onChange={({ detail }) => setIsEmptyPOLinesWarningAcknowledged(detail.checked)}
        checked={status === AsyncState.ERROR || status === AsyncState.OK ? true : isEmptyPOLinesWarningAcknowledged}
      >
        I acknowledge I will review the quantity in SC.os receipt.
      </Checkbox>
    </Alert>
  );
};

// TODO - needs to be refactored to a components
const MPNReceiveErrors = ({
  invalidContainers,
  containersNotInUserCustody,
  totalErrors,
  genericErrors,
}: MPNReceiveErrorsProps) => {
  return (
    <Alert statusIconAriaLabel="Error" type="error" header="Failed">
      <MPNReceieveCommonErrors otherErrors={genericErrors} />
      {invalidContainers.length > 0 && <InvalidContainersError invalidContainers={invalidContainers} />}
      {containersNotInUserCustody.length > 0 && (
        <ContainersNotinUserCustodyError containersNotInUserCustody={containersNotInUserCustody} />
      )}
      {/*
      if errors exists and not from RECEIVE_ERROR_MESSAGES_MAP
      if all errors are empty, but mpn receive status is set to zero
      */}
      {totalErrors >= 0 && <CommonErrorCreateCTI />}
    </Alert>
  );
};

export const MPNsToReceiveStatus = ({
  status,
  error,
  genericErrors,
  invalidContainers,
  containersNotInUserCustody,
  totalErrors,
  isAdjustment,
  isRMA,
  overrage,
  setIsEmptyPOLinesWarningAcknowledged,
  isEmptyPOLinesWarningAcknowledged,
  isRequireManualPOReceiveWarning,
  currentReceivedQty,
  isBlockingOverage,
  receivingQty,
  expectedQty,
}: MPNsToReceiveStatusProps) => {
  return (
    <div className="mpns-receive-status">
      {status === AsyncState.OK && <MPNReceiveSucess />}
      {error && <MPNReceiveGenericError />}
      {status === AsyncState.ERROR && (
        <MPNReceiveErrors
          genericErrors={genericErrors}
          invalidContainers={invalidContainers}
          containersNotInUserCustody={containersNotInUserCustody}
          totalErrors={totalErrors}
        />
      )}
      {!isAdjustment && !isRMA && overrage && receivingQty > 0 && (
        <MPNOverageWarning
          isBlockingOverage={isBlockingOverage}
          overageSerials={currentReceivedQty + receivingQty - expectedQty}
        />
      )}
      {isRequireManualPOReceiveWarning && (
        <MPNPOReceiveWarning
          status={status}
          setIsEmptyPOLinesWarningAcknowledged={setIsEmptyPOLinesWarningAcknowledged}
          isEmptyPOLinesWarningAcknowledged={isEmptyPOLinesWarningAcknowledged}
        />
      )}
    </div>
  );
};
